import { Modal } from '@nrx/ui';
import type { JSX } from 'react';

import type { GroupConsultDayBucket, GroupConsultEvent } from './group-consult-calendar.js';
import { GroupConsultDayPanel } from './group-consult-day-panel.js';

type GroupConsultDayOverlayProps = {
  selectedDay: GroupConsultDayBucket;
  timeZone: string;
  showRegisterButton: boolean;
  registerLoading: boolean;
  onRegisterClick: (event: GroupConsultEvent) => void;
  onClose: () => void;
};

/**
 * Slide-over holding the selected day's sessions, above the month grid.
 *
 * @remarks On `@nrx/ui`'s `Modal` (W4), which supplies the labelled `role="dialog"`, the focus
 * trap, Escape-to-close, body scroll lock and focus restore.
 *
 * What the hand-rolled version actually lacked was the focus trap, the scroll lock and focus
 * restore: it declared `aria-modal="true"` while letting Tab walk straight out into the page
 * behind. It DID close on Escape — `use-group-consults.ts` carried a `window` keydown listener
 * ported from the vendor, on the same guard condition as this overlay's render. That listener is
 * now deleted, because `Modal` binds Escape on `document` for precisely the same lifetime and
 * keeping both left one behaviour with two implementations and no coverage on the survivor.
 *
 * The slide-over geometry — bottom sheet under `md`, right-hand panel at `md` and up — comes from
 * `.consult-day-modal`, which sets only auto margins, `align-self` and max sizes on the modal
 * content. Those are box-model and positioning, which W3 permits on a library component; the
 * surface, elevation and radius stay `Modal`'s own.
 *
 * `aria-label` rather than `aria-labelledby` on the panel heading: the two page-level negatives
 * (`queryByRole('dialog', { name: 'Available sessions' })`) and the overlay's own positive
 * assertion all key on that exact name. Because the name no longer comes from the heading, the
 * panel's `titleId` prop had no consumer left and was deleted rather than left setting an id
 * nothing points at, which is what it had been doing before this task.
 */
export function GroupConsultDayOverlay({
  selectedDay,
  timeZone,
  showRegisterButton,
  registerLoading,
  onRegisterClick,
  onClose,
}: GroupConsultDayOverlayProps): JSX.Element {
  return (
    <Modal open onClose={onClose} aria-label="Available sessions" className="consult-day-modal">
      <GroupConsultDayPanel
        selectedDay={selectedDay}
        timeZone={timeZone}
        showRegisterButton={showRegisterButton}
        registerLoading={registerLoading}
        onRegisterClick={onRegisterClick}
        onClose={onClose}
      />
    </Modal>
  );
}
